import { Scope } from '@/app/core/scope/scope.service';

/**
 * Translates the current scope into a `filterBy` value for the `tenants` query (keyed on `id`).
 *
 * - `unbounded` → `undefined`: the caller omits `filterBy`, the query returns every tenant — byte-for-byte the
 *   pre-scope behavior.
 * - `bounded` → an `EQUALS` filter (single tenant) or an `OR` of `EQUALS` (a set). `FilterKind` has no native `IN`.
 *
 * NOTE: scope is a UX convenience, not a security boundary — the backend independently narrows every query to the
 * actor's authorized tenants, so an over-broad filter here can never surface unauthorized data.
 */
export function scopedTenantFilter(scope: Scope): Record<string, unknown>[] | undefined {
    if (scope.kind === 'unbounded') {
        return undefined;
    }

    const filters = scope.tenantIds.map((id) => ({ id: { kind: 'EQUALS', value: id } }));
    return filters.length <= 1 ? filters : [{ or: filters }];
}

/**
 * As {@link scopedTenantFilter} but keyed on `tenantId` for the `announcements` query, and — unlike the tenant
 * filter — it ALSO keeps global (null-tenant) announcements. This mirrors the backend resolver, which narrows the
 * `announcements` query with `ApplyToOrGlobal` (tenant-in-scope OR null-tenant). A bounded scope therefore emits an
 * `OR` that includes a `tenantId EQUALS null` clause; the backend filter builder translates `EQUALS null` on the
 * nullable `tenantId` column to SQL `IS NULL` (see Core `DataFilteringExtensions.GetActionExpression`). Unbounded
 * still omits the filter entirely (every announcement — byte-for-byte the pre-scope behavior).
 */
export function scopedAnnouncementFilter(scope: Scope): Record<string, unknown>[] | undefined {
    if (scope.kind === 'unbounded') {
        return undefined;
    }

    const tenantClauses = scope.tenantIds.map((id) => ({ tenantId: { kind: 'EQUALS', value: id } }));
    // Always wrap in `or` so the null-tenant (global) bucket rides alongside the scoped tenant(s).
    return [{ or: [...tenantClauses, { tenantId: { kind: 'EQUALS', value: null } }] }];
}

/**
 * Translates the current scope into a `filterBy` for the `userAccounts` query. Keyed on `tenant` — a MEMBERSHIP
 * filter (`UserAccountFilterInput.tenant`, which the backend maps to `TenantUserAccounts.Any(...)`), since a user has
 * no single tenant column. Like {@link scopedTenantFilter} (and unlike the announcement variant) there is no global
 * bucket — users are never tenant-less. `unbounded` → `undefined` (every user the actor may see, as before).
 *
 * NOTE: the backend independently narrows the users list to the actor's AUTHORIZED tenants; this only applies the
 * UI-selected (possibly narrower) scope, so it can never widen the result beyond what the actor may see.
 */
export function scopedUserFilter(scope: Scope): Record<string, unknown>[] | undefined {
    if (scope.kind === 'unbounded') {
        return undefined;
    }

    const filters = scope.tenantIds.map((id) => ({ tenant: { kind: 'EQUALS', value: id } }));
    return filters.length <= 1 ? filters : [{ or: filters }];
}
